import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, EmojiEvents } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { prizeService } from '../services/prizeService';
import { Prize } from '../types';
import { AppHeader } from '../components/AppHeader';

const PrizeList: React.FC = () => {
  const navigate = useNavigate();
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrizes();
  }, []);

  const fetchPrizes = async () => {
    try {
      const data = await prizeService.getAllPrizes();
      setPrizes(data);
    } catch (error) {
      console.error('Erro ao carregar prêmios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este prêmio?')) {
      try {
        await prizeService.deletePrize(id);
        await fetchPrizes();
      } catch (error) {
        console.error('Erro ao excluir prêmio:', error);
      }
    }
  };

  return (
    <>
      <AppHeader 
        title="Prêmios" 
        subtitle="Cadastre e gerencie prêmios incríveis"
        icon={<EmojiEvents />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Lista de Prêmios
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/prizes/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Novo Prêmio
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Carregando prêmios...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Campanha</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prizes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Nenhum prêmio encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Comece criando seu primeiro prêmio para ver a lista aqui.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('/prizes/new')}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          Criar Primeiro Prêmio
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  prizes.map((prize) => (
                    <TableRow key={prize.id}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {prize.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{prize.quantity}</TableCell>
                      <TableCell>{prize.campaign?.name || 'Sem campanha'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/prizes/${prize.id}/edit`)}
                            sx={{
                              borderRadius: 2,
                              '&:hover': { backgroundColor: 'primary.light' }
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(prize.id)}
                            color="error"
                            sx={{
                              borderRadius: 2,
                              '&:hover': { backgroundColor: 'error.light' }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </>
  );
};

export default PrizeList;